import type { ReactNode } from 'react';
import type { intelligence } from '@cwos/api-client';
import { ConsequenceNote, Pill } from '@cwos/ui-admin';
import {
  SEV_PILL,
  categoryLabel,
  describeSchedule,
  paramLabel,
} from '../../settings/rulesVocabulary.js';
import type { ResultAction } from './resultActions.js';

/**
 * A proposed automation, in the SAME three words the builder uses:
 * **WHEN / IF / DO**.
 *
 * That is the whole point of this card. `apps/admin/src/features/settings/
 * AutomationBuilder.tsx` teaches every administrator that an automation is a
 * trigger, some conditions and an effect, in that order and under those three
 * headings. An assistant that described the identical automation as "a rule
 * with parameters and an audience" would be teaching a second vocabulary for
 * one concept — the drift `rulesVocabulary.ts` exists to prevent — so this card
 * imports that vocabulary rather than restating it: the schedule reads through
 * `describeSchedule`, the parameters through `paramLabel`, the trigger through
 * `categoryLabel`, and the severity pill through `SEV_PILL`.
 *
 * NO UUID REACHES THE SCREEN. A team audience carries `subjectUserIds` and an
 * organization audience carries `programIds` / `siteIds` — opaque ids, all of
 * them. The card renders their COUNT and the reach in words ("the workers you
 * supervise at the moment it runs"), which is both the honest description and
 * the one a person can act on. Printing the ids would be printing a key at
 * someone and calling it an audience.
 *
 * NOTHING HERE SAVES. `automation.plan` is plan-only and persists nothing; the
 * plan identity (`planId` + `diffDigest`) exists so a confirmation can bind to
 * this exact change, and that ceremony belongs to the turn surface. When the
 * payload carries `execution`, its honesty line is relayed VERBATIM — a created
 * automation that cannot fire yet must never be presented as active.
 *
 * Absent payload → renders nothing.
 */
export function AutomationCard({
  automation,
  onAction,
}: {
  automation: intelligence.ResultAutomation | null | undefined;
  /**
   * Where [Review automation] goes. Absent on a surface that has not wired the
   * conversation yet, in which case no button is drawn — see
   * `resultActions.ts`. The button ASKS to look properly; it approves nothing,
   * and the confirmation ceremony is unchanged and still elsewhere.
   */
  onAction?: (action: ResultAction) => void;
}) {
  if (automation === null || automation === undefined) return null;

  const request = automation.validatedRequest;
  const params = Object.entries(request.params);

  return (
    <section className="rn-card" aria-label={`Automation: ${request.ruleName}`}>
      <div className="rn-card-head">
        <h3 className="rn-card-title">{request.ruleName}</h3>
        {automation.severity !== undefined ? (
          <Pill variant={SEV_PILL[automation.severity] ?? 'plain'}>{automation.severity}</Pill>
        ) : null}
      </div>

      <p className="rn-muted" style={{ marginTop: 0 }}>
        {TIER_BLURB[request.tier]}
      </p>

      <Clause label="WHEN">
        <p style={{ margin: 0 }}>
          {categoryLabel(request.ruleType)} — checked{' '}
          {describeSchedule({ kind: 'cron', expression: request.trigger.expression }).toLowerCase()}
          .
        </p>
      </Clause>

      <Clause label="IF — conditions">
        {params.length === 0 ? (
          <p className="rn-muted" style={{ margin: 0 }}>
            No conditions — this watches for the trigger itself.
          </p>
        ) : (
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              columnGap: 'var(--space-12)',
              rowGap: 'var(--space-2)',
              margin: 0,
            }}
          >
            {params.map(([key, value]) => (
              <div key={key} style={{ display: 'contents' }}>
                <dt className="rn-muted" style={{ margin: 0 }}>
                  {paramLabel(key)}
                </dt>
                <dd style={{ margin: 0 }}>{value}</dd>
              </div>
            ))}
          </dl>
        )}
      </Clause>

      <Clause label="DO — what happens on a match">
        {/* The builder's own sentence, verbatim: a match always raises an
            exception, and that is not a setting anyone can turn off. */}
        <p style={{ margin: 0 }}>A match always raises an exception in Needs attention.</p>
        <p className="rn-muted" style={{ margin: 'var(--space-6) 0 0' }}>
          {audienceSentence(request.audience)}
        </p>
        <p className="rn-muted" style={{ margin: 'var(--space-4) 0 0' }}>
          {ATTRIBUTION_SENTENCE[request.attribution.model]}
        </p>
      </Clause>

      {automation.explanation !== undefined ? (
        <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
          {automation.explanation}
        </p>
      ) : null}

      {automation.reasons.length > 0 ? (
        <ul style={{ margin: 'var(--space-10) 0 0', paddingLeft: 'var(--space-20)' }}>
          {automation.reasons.map((reason) => (
            <li key={reason} className="rn-muted">
              {reason}
            </li>
          ))}
        </ul>
      ) : null}

      {!automation.persistable ? (
        <p className="tc-warn" style={{ marginTop: 'var(--space-10)' }} role="alert">
          This cannot be saved as described. The notes above say what stands in the way.
        </p>
      ) : null}

      {automation.execution !== undefined && !automation.execution.canExecuteNow ? (
        // Relayed verbatim from the server's own honesty field, never
        // paraphrased: "saved" and "running" are different claims, and this is
        // the sentence that keeps them apart.
        <p className="rn-muted" style={{ marginTop: 'var(--space-10)' }} role="status">
          {automation.execution.reason}
        </p>
      ) : null}

      {onAction !== undefined ? (
        <div style={{ marginTop: 'var(--space-12)' }}>
          <button
            type="button"
            className="msg-primary"
            onClick={() =>
              // Name only (F290): the action becomes one sentence, and
              // `automation.planId` is the server's plan identity, which the
              // confirmation ceremony binds — not this button.
              onAction({ kind: 'review_automation', name: request.ruleName })
            }
          >
            Review automation
          </button>
        </div>
      ) : null}

      <ConsequenceNote text="Nothing has been created. This is what would be set up, waiting on a confirmation that names exactly this change." />
    </section>
  );
}

function Clause({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ marginTop: 'var(--space-12)' }}>
      <span
        className="rn-muted"
        style={{ textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.04em' }}
      >
        {label}
      </span>
      <div style={{ marginTop: 'var(--space-4)' }}>{children}</div>
    </div>
  );
}

/**
 * Reach, in the builder's own words — and as COUNTS where the payload carries
 * ids. `subjectUserIds`, `programIds` and `siteIds` are opaque; how many there
 * are is a fact a person can use, and which uuids they are is not.
 */
function audienceSentence(audience: intelligence.AutomationAudience): string {
  switch (audience.kind) {
    case 'self':
      return 'Reaches you alone. That is built into a personal automation, not a setting on it.';
    case 'team': {
      const n = audience.subjectUserIds.length;
      return `Reaches ${n} ${n === 1 ? 'person' : 'people'} you supervise at the moment it runs. That is checked again every time it fires, and it pauses on its own if you lose that authority.`;
    }
    case 'organization': {
      const programs = audience.programIds?.length ?? 0;
      const sites = audience.siteIds?.length ?? 0;
      if (programs === 0 && sites === 0) {
        return 'An organization rule — it behaves exactly like the rules on the Automations page always have.';
      }
      const parts: string[] = [];
      if (programs > 0) parts.push(`${programs} ${programs === 1 ? 'program' : 'programs'}`);
      if (sites > 0) parts.push(`${sites} ${sites === 1 ? 'site' : 'sites'}`);
      return `An organization rule, scoped to ${parts.join(' and ')}.`;
    }
  }
}

/** Delivery identity — a closed pair, matching `automationAttributionSchema`. */
const ATTRIBUTION_SENTENCE: Readonly<Record<intelligence.AutomationAttribution['model'], string>> =
  {
    owner: 'Delivered as “Automated · set up by” its owner.',
    organization: 'Delivered as “Automated · organization rule”.',
  };

/** The tier, said the way the builder says it. */
const TIER_BLURB: Readonly<Record<intelligence.AutomationTier, string>> = {
  personal: 'Personal — yours alone.',
  team: 'Team — the people you supervise.',
  organization: 'Organization — everyone it applies to.',
};
